import { displayDateTimeToMinute, Entity, FieldType, OptionsField, PersistenceType } from 'gossamer-unified';
import { Institution } from './Institution';
import { Event } from './Event';

export interface ReportEventViewRow {
  event_id: string;
  institution_id: number;
  start_at: string;
  flow_option: string;
  booking_count: number;
  capacity: number;
  utilisation_rate: string;
  attendance_count: number;
  attendance_rate: string;
  sum_content_score: number;
  avg_content_score: number;
  sum_delivery_score: number;
  avg_delivery_score: number;
  sum_admin_score: number;
  avg_admin_score: number;
  sum_overall_score: number;
  avg_overall_score: number;
  feedback_count: number;
}

export const ReportEventView: Entity<ReportEventViewRow> = {
  id: 'ReportEventView',
  title: 'Report Event View',
  service: 'main',
  rowLabel: (row: ReportEventViewRow) => [row.event_id, row.start_at].join(' / '),
  primaryKeyFields: ['event_id'],
  source: {
    tableName: 'public.report_event_view',
  },
  security: [
    { role: 'user', action: 'read', access: 'conditional', condition: "user_id = '{userId}'" },
    { role: 'admin', action: 'read', access: 'conditional', condition: 'institution_id = {adminInstitutionId}' },
    { role: 'staff', action: 'read', access: 'all' },
  ],
  defaultSort: [['start_at', 'DESC']],
  defaultFilters: [
    { fieldId: 'event_id', operator: 'eq', value: '' },
    { fieldId: 'start_at', operator: 'nb', value: '' },
  ],
  listRowLink: (key: string) => {
    return (
      '/admin/reporting/booking-search?q=' +
      encodeURIComponent(
        JSON.stringify({
          where: {
            and: [{ fieldId: 'event_id', operator: 'eq', value: key }],
          },
        }),
      )
    );
  },
  fields: {
    event_id: {
      seq: 5,
      label: 'Event',
      type: FieldType.Reference,
      refEntity: Event,
    },
    institution_id: {
      seq: 10,
      label: 'Institution',
      type: FieldType.Reference,
      refEntity: Institution,
    },
    start_at: {
      seq: 15,
      type: FieldType.Datetime,
      label: 'Event Start',
      listColumn: true,
      // listColumnAlign: 'right',
      getDisplayText: (value: string) => displayDateTimeToMinute(value, '-'),
      listColumnWidth: 125,
    },
    flow_option: {
      seq: 20,
      label: 'Flow Option',
      type: FieldType.Options,
      options: (Event.fields.flow_option as OptionsField).options,
    },
    booking_count: {
      seq: 25,
      label: 'Bookings',
      type: FieldType.Integer,
      listColumn: true,
    },
    capacity: {
      seq: 30,
      label: 'Capacity',
      type: FieldType.Integer,
      listColumn: true,
    },
    utilisation_rate: {
      seq: 35,
      label: 'Utilisation Rate',
      type: FieldType.Text,
      listColumn: true,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportEventViewRow) =>
        !row.capacity ? '-' : ((row.booking_count * 100) / row.capacity).toFixed(0) + '%',
    },
    attendance_count: {
      seq: 40,
      label: 'Attendances',
      type: FieldType.Integer,
      listColumn: true,
    },
    attendance_rate: {
      seq: 45,
      label: 'Attendance Rate',
      type: FieldType.Text,
      listColumn: true,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportEventViewRow) =>
        !row.booking_count ? '-' : ((row.attendance_count * 100) / row.booking_count).toFixed(0) + '%',
    },
    sum_content_score: {
      seq: 50,
      label: 'Total Content Score',
      type: FieldType.Decimal,
    },
    avg_content_score: {
      seq: 55,
      label: 'Average Content Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportEventViewRow) =>
        !row.feedback_count ? '-' : (row.sum_content_score / row.feedback_count).toFixed(1),
    },
    sum_delivery_score: {
      seq: 60,
      label: 'Total Delivery Score',
      type: FieldType.Decimal,
    },
    avg_delivery_score: {
      seq: 65,
      label: 'Average Delivery Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportEventViewRow) =>
        !row.feedback_count ? '-' : (row.sum_delivery_score / row.feedback_count).toFixed(1),
    },
    sum_admin_score: {
      seq: 70,
      label: 'Total Admin Score',
      type: FieldType.Decimal,
    },
    avg_admin_score: {
      seq: 75,
      label: 'Average Admin Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportEventViewRow) =>
        !row.feedback_count ? '-' : (row.sum_content_score / row.feedback_count).toFixed(1),
    },
    sum_overall_score: {
      seq: 80,
      label: 'Total Overall Score',
      type: FieldType.Decimal,
    },
    avg_overall_score: {
      seq: 85,
      label: 'Average Overall Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportEventViewRow) =>
        !row.feedback_count ? '-' : (row.sum_content_score / row.feedback_count).toFixed(1),
    },
    feedback_count: {
      seq: 90,
      label: 'Feedback',
      type: FieldType.Integer,
      listColumn: true,
    },
  },
};
